import React, { useState, useEffect } from 'react';
import Modal from '../../Components/Modal';
import Button from '../../Components/Button';
import Snackbar from '../../Components/Snackbar';
import {
  getMarketPlace,
  createMarketPlace,
  deleteMarketPlace,
  updateMarketPlace
} from '../../Utils/apiServices';
import ConfirmDialog from '../../Components/ConfirmDialog';
import { Box, Typography } from '@mui/material';

const MarketPlace = () => {
  const [marketplaceItems, setMarketplaceItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ isOpen: false, message: '', type: 'success' });
  const [deleteListingId, setDeleteListingId] = useState(null);
  const [deleteListingDialogOpen, setDeleteListingDialogOpen] = useState(false);
  const [deleteListingLoading, setDeleteListingLoading] = useState(false);
  
  // Form state for create/edit
  const [formData, setFormData] = useState({
    horse_name: '',
    userid: null,
    horse_identification_number: '',
    horse_birth_year: '',
    horse_gender: '',
    horse_level: '',
    horse_breed: '',
    horse_color: '',
    horse_height_cm: '',
    horse_description: '',
    horse_discipline: '',
    vet_records: '',
    price: '',
    type: '',
    horse_location: '',
    sire_name: '',
    sire_identification_number: '',
    dam_sire_name: '',
    dam_sire_identification_number: '',
    horse_photos: '',
    horse_videos: '',
    youtube_link: '',
    seller_name: '',
    seller_email: '',
    seller_phone: '',
    terms_and_conditions: ''
  });

  // Fetch marketplace items
  const fetchMarketplaceItems = async () => {
    setLoading(true);
    try {
      const response = await getMarketPlace();
      if (response.data.success) {
        setMarketplaceItems(response.data.data);
      }
    } catch (error) {
      showSnackbar('Failed to fetch marketplace items', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketplaceItems();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      horse_name: '',
      userid: null,
      horse_identification_number: '',
      horse_birth_year: '',
      horse_gender: '',
      horse_level: '',
      horse_breed: '',
      horse_color: '',
      horse_height_cm: '',
      horse_description: '',
      horse_discipline: '',
      vet_records: '',
      price: '',
      type: '',
      horse_location: '',
      sire_name: '',
      sire_identification_number: '',
      dam_sire_name: '',
      dam_sire_identification_number: '',
      horse_photos: '',
      horse_videos: '',
      youtube_link: '',
      seller_name: '',
      seller_email: '',
      seller_phone: '',
      terms_and_conditions: ''
    });
  };

  // Create new marketplace item
  const handleCreate = async () => {
    try {
      const response = await createMarketPlace(formData);
      if (response.data.success) {
        showSnackbar('Marketplace item created successfully', 'success');
        setIsCreateModalOpen(false);
        resetForm();
        fetchMarketplaceItems();
      }
    } catch (error) {
      showSnackbar('Failed to create marketplace item', 'error');
    }
  };

  // Update marketplace item
  const handleUpdate = async () => {
    try {
      const response = await updateMarketPlace(formData);
      if (response.data.success) {
        showSnackbar('Marketplace item updated successfully', 'success');
        setIsEditModalOpen(false);
        resetForm();
        fetchMarketplaceItems();
      }
    } catch (error) {
      showSnackbar('Failed to update marketplace item', 'error');
    }
  };

  const openDeleteListingConfirm = (id) => {
    setDeleteListingId(id);
    setDeleteListingDialogOpen(true);
  };

  const closeDeleteListingConfirm = () => {
    if (deleteListingLoading) return;
    setDeleteListingDialogOpen(false);
    setDeleteListingId(null);
  };

  const confirmDeleteListing = async () => {
    if (deleteListingId == null) return;
    setDeleteListingLoading(true);
    try {
      const response = await deleteMarketPlace(deleteListingId);
      if (response.data.success) {
        showSnackbar('Marketplace item deleted successfully', 'success');
        setDeleteListingDialogOpen(false);
        setDeleteListingId(null);
        fetchMarketplaceItems();
      }
    } catch (error) {
      showSnackbar('Failed to delete marketplace item', 'error');
    } finally {
      setDeleteListingLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (item) => {
    setFormData(item);
    setIsEditModalOpen(true);
  };

  // Show snackbar
  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ isOpen: true, message, type });
    setTimeout(() => setSnackbar({ isOpen: false, message: '', type: 'success' }), 3000);
  };

  // Enhanced Form field component
  const FormField = ({ label, name, type = 'text', required = false, placeholder = '' }) => (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ 
        display: 'block', 
        marginBottom: '8px', 
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
      }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        value={formData[name] || ''}
        onChange={handleInputChange}
        style={{
          width: '100%',
          padding: '12px 16px',
          border: '2px solid #e5e7eb',
          borderRadius: '8px',
          fontSize: '14px',
          transition: 'all 0.2s ease',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#3b82f6';
          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#e5e7eb';
          e.target.style.boxShadow = 'none';
        }}
      />
    </div>
  );

  // Enhanced Textarea component
  const FormTextarea = ({ label, name, placeholder = '', rows = 4 }) => (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ 
        display: 'block', 
        marginBottom: '8px', 
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
      }}>
        {label}
      </label>
      <textarea
        name={name}
        placeholder={placeholder}
        rows={rows}
        value={formData[name] || ''}
        onChange={handleInputChange}
        style={{
          width: '100%',
          padding: '12px 16px',
          border: '2px solid #e5e7eb',
          borderRadius: '8px',
          fontSize: '14px',
          transition: 'all 0.2s ease',
          backgroundColor: '#ffffff',
          resize: 'vertical',
          minHeight: '100px',
          fontFamily: 'inherit',
          boxSizing: 'border-box'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#3b82f6';
          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#e5e7eb';
          e.target.style.boxShadow = 'none';
        }}
      />
    </div>
  );

  return (
    <div style={{ padding: '0' }}>
      <ConfirmDialog
        open={deleteListingDialogOpen}
        title="Delete marketplace listing"
        message="Are you sure you want to delete this listing? This action cannot be undone."
        confirmLabel="Delete"
        onCancel={closeDeleteListingConfirm}
        onConfirm={confirmDeleteListing}
        loading={deleteListingLoading}
      />

      {/* Gradient Banner */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f97316 0%, #ec4899 50%, #8b5cf6 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 8px 24px rgba(249,115,22,0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.3 }}>🐎 Marketplace</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>Manage horse listings and marketplace items</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>{marketplaceItems.length}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Listings</Typography>
          </Box>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              borderRadius: '8px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.4)',
              cursor: 'pointer',
              backdropFilter: 'blur(4px)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'; }}
          >
            + Create New Item
          </button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #f97316, #ea580c)',
          padding: '20px 24px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(249,115,22,0.3)',
          color: '#fff',
        }}>
          <div style={{ fontSize: '28px', fontWeight: '800', lineHeight: 1 }}>{marketplaceItems.length}</div>
          <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Items</div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #10b981, #059669)',
          padding: '20px 24px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
          color: '#fff',
        }}>
          <div style={{ fontSize: '28px', fontWeight: '800', lineHeight: 1 }}>{marketplaceItems.filter(item => item.price).length}</div>
          <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Priced Items</div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
          padding: '20px 24px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(139,92,246,0.3)',
          color: '#fff',
        }}>
          <div style={{ fontSize: '28px', fontWeight: '800', lineHeight: 1 }}>{marketplaceItems.filter(item => item.horse_location).length}</div>
          <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>With Location</div>
        </div>
      </div>

      {/* Marketplace Items Table */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827'
          }}>
            Marketplace Items
          </h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{ 
                  padding: '16px 20px', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Horse Name</th>
                <th style={{ 
                  padding: '16px 20px', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Breed</th>
                <th style={{ 
                  padding: '16px 20px', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Price</th>
                <th style={{ 
                  padding: '16px 20px', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Location</th>
                <th style={{ 
                  padding: '16px 20px', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Created</th>
                <th style={{ 
                  padding: '16px 20px', 
                  textAlign: 'center', 
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ 
                    padding: '48px 24px', 
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #e5e7eb',
                        borderTop: '2px solid #3b82f6',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Loading marketplace items...
                    </div>
                  </td>
                </tr>
              ) : marketplaceItems.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ 
                    padding: '48px 24px', 
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}>
                    <div style={{ marginBottom: '16px' }}>🐎</div>
                    <div style={{ fontWeight: '500', marginBottom: '8px' }}>No marketplace items found</div>
                    <div>Create your first marketplace item to get started</div>
                  </td>
                </tr>
              ) : (
                marketplaceItems.map((item, index) => (
                  <tr key={item.id} style={{ 
                    borderBottom: '1px solid #f1f5f9',
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafbfc',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#fafbfc';
                  }}>
                    <td style={{ padding: '16px 20px' }}>
                      <span 
                        style={{ 
                          color: '#3b82f6', 
                          cursor: 'pointer',
                          fontWeight: '500',
                          transition: 'color 0.2s ease'
                        }}
                        onClick={() => {
                          setSelectedItem(item);
                          setIsDetailModalOpen(true);
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#2563eb';
                          e.target.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#3b82f6';
                          e.target.style.textDecoration = 'none';
                        }}
                      >
                        {item.horse_name || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: '16px 20px', color: '#374151' }}>
                      {item.horse_breed || 'N/A'}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      {item.price ? (
                        <span style={{
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          ${item.price}
                        </span>
                      ) : (
                        <span style={{ color: '#6b7280' }}>N/A</span>
                      )}
                    </td>
                    <td style={{ padding: '16px 20px', color: '#374151' }}>
                      {item.horse_location || 'N/A'}
                    </td>
                    <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: '13px' }}>
                      {new Date(item.createdat).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button 
                          onClick={() => openEditModal(item)}
                          style={{ 
                            padding: '6px 12px', 
                            fontSize: '12px',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontWeight: '500'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#e5e7eb';
                            e.target.style.borderColor = '#9ca3af';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#f3f4f6';
                            e.target.style.borderColor = '#d1d5db';
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          onClick={() => openDeleteListingConfirm(item.id)}
                          style={{ 
                            padding: '6px 12px', 
                            fontSize: '12px',
                            backgroundColor: '#fef2f2',
                            color: '#dc2626',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontWeight: '500'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#fee2e2';
                            e.target.style.borderColor = '#fca5a5';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#fef2f2';
                            e.target.style.borderColor = '#fecaca';
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Marketplace Item Details"
        actions={
          <Button onClick={() => setIsDetailModalOpen(false)}>Close</Button>
        }
      >
        {selectedItem && (
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '20px',
              marginBottom: '24px'
            }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Horse Name</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.horse_name || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Breed</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.horse_breed || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Gender</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.horse_gender || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Birth Year</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.horse_birth_year || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Color</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.horse_color || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Height</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.horse_height_cm ? `${selectedItem.horse_height_cm}cm` : 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Level</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.horse_level || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Discipline</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.horse_discipline || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Price</div>
                <div style={{ 
                  color: selectedItem.price ? '#166534' : '#6b7280',
                  fontWeight: selectedItem.price ? '600' : 'normal'
                }}>
                  {selectedItem.price ? `$${selectedItem.price}` : 'N/A'}
                </div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Location</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.horse_location || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Type</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.type || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Seller Name</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.seller_name || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Seller Email</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.seller_email || 'N/A'}</div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Seller Phone</div>
                <div style={{ color: '#6b7280' }}>{selectedItem.seller_phone || 'N/A'}</div>
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Description</div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                {selectedItem.horse_description || 'No description available'}
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Vet Records</div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                {selectedItem.vet_records || 'No vet records available'}
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Terms & Conditions</div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                {selectedItem.terms_and_conditions || 'No terms available'}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          resetForm();
        }}
        title="Create New Marketplace Item"
        actions={
          <>
            <Button variant="secondary" onClick={() => {
              setIsCreateModalOpen(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create</Button>
          </>
        }
      >
        <div style={{ maxHeight: '600px', overflowY: 'auto', padding: '8px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '20px',
            marginBottom: '20px'
          }}>
            <FormField label="Horse Name" name="horse_name" required placeholder="Enter horse name" />
            <FormField label="Horse ID Number" name="horse_identification_number" placeholder="Enter identification number" />
            <FormField label="Birth Year" name="horse_birth_year" type="number" placeholder="e.g., 2018" />
            <FormField label="Gender" name="horse_gender" placeholder="Male/Female" />
            <FormField label="Level" name="horse_level" placeholder="Beginner/Intermediate/Advanced" />
            <FormField label="Breed" name="horse_breed" placeholder="e.g., Thoroughbred" />
            <FormField label="Color" name="horse_color" placeholder="e.g., Bay, Chestnut" />
            <FormField label="Height (cm)" name="horse_height_cm" type="number" placeholder="e.g., 160" />
            <FormField label="Discipline" name="horse_discipline" placeholder="e.g., Dressage, Jumping" />
            <FormField label="Price" name="price" type="number" placeholder="Enter price in USD" />
            <FormField label="Type" name="type" placeholder="e.g., Sale, Lease" />
            <FormField label="Location" name="horse_location" placeholder="City, State" />
            <FormField label="Sire Name" name="sire_name" placeholder="Father's name" />
            <FormField label="Sire ID" name="sire_identification_number" placeholder="Father's ID" />
            <FormField label="Dam Sire Name" name="dam_sire_name" placeholder="Mother's father's name" />
            <FormField label="Dam Sire ID" name="dam_sire_identification_number" placeholder="Mother's father's ID" />
            <FormField label="Horse Photos" name="horse_photos" placeholder="Photo URLs (comma separated)" />
            <FormField label="Horse Videos" name="horse_videos" placeholder="Video URLs (comma separated)" />
            <FormField label="YouTube Link" name="youtube_link" placeholder="YouTube video URL" />
            <FormField label="Seller Name" name="seller_name" placeholder="Your name" />
            <FormField label="Seller Email" name="seller_email" type="email" placeholder="your@email.com" />
            <FormField label="Seller Phone" name="seller_phone" placeholder="Phone number" />
          </div>
          
          <FormTextarea 
            label="Description" 
            name="horse_description" 
            placeholder="Describe the horse's characteristics, training, and any special features..."
            rows={4}
          />
          <FormTextarea 
            label="Vet Records" 
            name="vet_records" 
            placeholder="Include any veterinary records, health certificates, or medical history..."
            rows={4}
          />
          <FormTextarea 
            label="Terms & Conditions" 
            name="terms_and_conditions" 
            placeholder="Specify any terms and conditions for the sale or lease..."
            rows={4}
          />
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          resetForm();
        }}
        title="Edit Marketplace Item"
        actions={
          <>
            <Button variant="secondary" onClick={() => {
              setIsEditModalOpen(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Update</Button>
          </>
        }
      >
        <div style={{ maxHeight: '600px', overflowY: 'auto', padding: '8px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '20px',
            marginBottom: '20px'
          }}>
            <FormField label="Horse Name" name="horse_name" required placeholder="Enter horse name" />
            <FormField label="Horse ID Number" name="horse_identification_number" placeholder="Enter identification number" />
            <FormField label="Birth Year" name="horse_birth_year" type="number" placeholder="e.g., 2018" />
            <FormField label="Gender" name="horse_gender" placeholder="Male/Female" />
            <FormField label="Level" name="horse_level" placeholder="Beginner/Intermediate/Advanced" />
            <FormField label="Breed" name="horse_breed" placeholder="e.g., Thoroughbred" />
            <FormField label="Color" name="horse_color" placeholder="e.g., Bay, Chestnut" />
            <FormField label="Height (cm)" name="horse_height_cm" type="number" placeholder="e.g., 160" />
            <FormField label="Discipline" name="horse_discipline" placeholder="e.g., Dressage, Jumping" />
            <FormField label="Price" name="price" type="number" placeholder="Enter price in USD" />
            <FormField label="Type" name="type" placeholder="e.g., Sale, Lease" />
            <FormField label="Location" name="horse_location" placeholder="City, State" />
            <FormField label="Sire Name" name="sire_name" placeholder="Father's name" />
            <FormField label="Sire ID" name="sire_identification_number" placeholder="Father's ID" />
            <FormField label="Dam Sire Name" name="dam_sire_name" placeholder="Mother's father's name" />
            <FormField label="Dam Sire ID" name="dam_sire_identification_number" placeholder="Mother's father's ID" />
            <FormField label="Horse Photos" name="horse_photos" placeholder="Photo URLs (comma separated)" />
            <FormField label="Horse Videos" name="horse_videos" placeholder="Video URLs (comma separated)" />
            <FormField label="YouTube Link" name="youtube_link" placeholder="YouTube video URL" />
            <FormField label="Seller Name" name="seller_name" placeholder="Your name" />
            <FormField label="Seller Email" name="seller_email" type="email" placeholder="your@email.com" />
            <FormField label="Seller Phone" name="seller_phone" placeholder="Phone number" />
          </div>
          
          <FormTextarea 
            label="Description" 
            name="horse_description" 
            placeholder="Describe the horse's characteristics, training, and any special features..."
            rows={4}
          />
          <FormTextarea 
            label="Vet Records" 
            name="vet_records" 
            placeholder="Include any veterinary records, health certificates, or medical history..."
            rows={4}
          />
          <FormTextarea 
            label="Terms & Conditions" 
            name="terms_and_conditions" 
            placeholder="Specify any terms and conditions for the sale or lease..."
            rows={4}
          />
        </div>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        isOpen={snackbar.isOpen}
        message={snackbar.message}
        type={snackbar.type}
      />

      {/* CSS for loading animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MarketPlace;